import { useState, useMemo, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import {
  Fuel as FuelIcon, Upload, AlertTriangle, ArrowLeft, Search, ArrowRightLeft, ShieldAlert, TrendingDown, Clock, Trophy, MessageSquare, FileText,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { useFuelTransactions, useFuelDashboard } from '@/hooks/useFuel';
import { useQueryClient } from '@tanstack/react-query';
import { ProjectSwitcher } from '@/components/common/ProjectSwitcher';
import { useCurrentProject } from '@/contexts/CurrentProjectContext';
import { RowDeleteButton } from '@/components/common/RowDeleteButton';
import { FuelTransactionDetailSheet } from '@/components/fuel/FuelTransactionDetailSheet';

type DateFilter = 'today' | 'week' | 'month' | 'all';

function buildDateRange(f: DateFilter): { from?: string; to?: string } {
  if (f === 'all') return {};
  const now = new Date();
  const to = new Date(now);
  const from = new Date(now);
  if (f === 'today') from.setHours(0, 0, 0, 0);
  else if (f === 'week') from.setDate(from.getDate() - 7);
  else if (f === 'month') from.setMonth(from.getMonth() - 1);
  return { from: from.toISOString(), to: to.toISOString() };
}

export default function FuelPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [dateFilter, setDateFilter] = useState<DateFilter>('month');
  const [providerFilter, setProviderFilter] = useState<string>('');
  const [hasMovement, setHasMovement] = useState<string>('');
  const [search, setSearch] = useState('');
  const [openTxId, setOpenTxId] = useState<string | null>(null);

  // The side travels on the X-Project-Id header, which react-query cannot see.
  // Without it in the key a switch would serve the previous side's rows from
  // cache - a filter that looks like it worked while showing the wrong fleet.
  const { project } = useCurrentProject();

  const filters = useMemo(() => {
    const dr = buildDateRange(dateFilter);
    const f: Record<string, any> = { pageSize: 100, ...dr };
    if (providerFilter) f.provider = providerFilter;
    if (hasMovement) f.has_movement = hasMovement;
    if (search) f.q = search;
    // Cache discriminator only. The server reads the header itself; this exists
    // so the key changes when the side does.
    f._side = project?.id ?? 'all';
    return f;
  }, [dateFilter, providerFilter, hasMovement, search, project?.id]);

  const { data: list, isLoading } = useFuelTransactions(filters);
  const qc = useQueryClient();
  const refetchFuel = () => qc.invalidateQueries({ queryKey: ['fuel', 'transactions'] });
  const { data: stats } = useFuelDashboard();

  const fmtSar = (n: number | null) => (n == null ? '—' : n.toLocaleString(undefined, { maximumFractionDigits: 2 }));

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            {t('common.back')}
          </Button>
          <div className="flex items-center gap-2">
            <FuelIcon className="h-5 w-5 text-orange-500" />
            <h1 className="text-lg font-semibold">{t('fuel.title')}</h1>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Link to="/dashboard/fuel/consumption" data-prw1="consumption-button">
              <Button variant="outline" size="sm" className="gap-2">
                <TrendingDown className="h-4 w-4 text-amber-500" />
                Consumption
              </Button>
            </Link>
            <Link to="/dashboard/fuel/idle-waste" data-prw2="idle-waste-button">
              <Button variant="outline" size="sm" className="gap-2">
                <Clock className="h-4 w-4 text-amber-500" />
                Idle waste
              </Button>
            </Link>
            <Link to="/dashboard/fuel/driver-leaderboard" data-prw3="driver-leaderboard-button">
              <Button variant="outline" size="sm" className="gap-2">
                <Trophy className="h-4 w-4 text-amber-500" />
                Drivers
              </Button>
            </Link>
            <Link to="/dashboard/fuel/coaching" data-prw4="coaching-button">
              <Button variant="outline" size="sm" className="gap-2">
                <MessageSquare className="h-4 w-4 text-amber-500" />
                Coaching
              </Button>
            </Link>
            <Link to="/dashboard/fuel/monthly-reports" data-prw4="reports-button">
              <Button variant="outline" size="sm" className="gap-2">
                <FileText className="h-4 w-4 text-amber-500" />
                Reports
              </Button>
            </Link>
            <Link to="/dashboard/anomalies">
              <Button variant="outline" size="sm" className="gap-2">
                <ShieldAlert className="h-4 w-4 text-red-500" />
                {t('anomalies.title')}
              </Button>
            </Link>
            <Link to="/dashboard/fuel/card-movements">
              <Button variant="outline" size="sm" className="gap-2">
                <ArrowRightLeft className="h-4 w-4" />
                {t('fuel.cardMovements')}
                {stats?.pending_card_movements ? (
                  <Badge className="ml-1 bg-amber-500/15 text-amber-700 border-amber-500/30">
                    {stats.pending_card_movements}
                  </Badge>
                ) : null}
              </Button>
            </Link>
            <Link to="/dashboard/fuel/import">
              <Button size="sm" className="gap-2 bg-gold text-gold-foreground hover:bg-gold/90">
                <Upload className="h-4 w-4" />
                {t('fuel.importButton')}
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-4">
        {/* Stats strip */}
        {stats ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label={t('fuel.stats.last30Sar')} value={fmtSar(stats.month_sar) + ' SAR'} />
            <StatCard label={t('fuel.stats.last30Liters')} value={fmtSar(stats.month_liters) + ' L'} />
            <StatCard label={t('fuel.stats.last30Count')} value={String(stats.month_count)} />
            <StatCard
              label={t('fuel.stats.pendingMovements')}
              value={String(stats.pending_card_movements)}
              warn={stats.pending_card_movements > 0}
            />
          </div>
        ) : null}

        {/* Which side's fuel this is. The API has always filtered on it; the page
            never said so and gave no way to change it. */}
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <ProjectSwitcher />
          <span className="text-xs text-muted-foreground">
            Only vehicles filed under this side. Change it on Settings &gt; Sides.
          </span>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2">
          {(['today', 'week', 'month', 'all'] as DateFilter[]).map((f) => (
            <Button
              key={f}
              variant={dateFilter === f ? 'default' : 'outline'}
              size="sm"
              onClick={() => setDateFilter(f)}
            >
              {t(`fuel.filters.${f}`)}
            </Button>
          ))}
          <select
            value={providerFilter}
            onChange={(e) => setProviderFilter(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="">{t('fuel.filters.allProviders')}</option>
            <option value="petroapp">PetroApp</option>
            <option value="aldrees">Aldrees</option>
            <option value="aldrees_monthly">Aldrees (monthly)</option>
            <option value="aldrees_daily">Aldrees (daily)</option>
            <option value="manual">{t('fuel.filters.manual')}</option>
          </select>
          <select
            value={hasMovement}
            onChange={(e) => setHasMovement(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="">{t('fuel.filters.allMovements')}</option>
            <option value="true">{t('fuel.filters.withMovement')}</option>
            <option value="false">{t('fuel.filters.withoutMovement')}</option>
          </select>
          <div className="flex-1 min-w-[200px] relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={t('fuel.filters.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {/* List */}
        {isLoading ? (
          <div className="text-center text-muted-foreground py-12">{t('common.loading')}</div>
        ) : !list?.items.length ? (
          <div className="text-center py-16 border border-dashed border-border rounded-md">
            <FuelIcon className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
            <p className="text-muted-foreground">{t('fuel.empty.title')}</p>
            <p className="text-sm text-muted-foreground mt-1">{t('fuel.empty.subtitle')}</p>
            <Link to="/dashboard/fuel/import" className="inline-block mt-4">
              <Button size="sm" className="gap-2 bg-gold text-gold-foreground hover:bg-gold/90">
                <Upload className="h-4 w-4" />
                {t('fuel.importButton')}
              </Button>
            </Link>
          </div>
        ) : (
          <div className="border border-border rounded-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <Th>{t('fuel.table.date')}</Th>
                    <Th>{t('fuel.table.provider')}</Th>
                    <Th>{t('fuel.table.vehicle')}</Th>
                    <Th>{t('fuel.table.driver')}</Th>
                    <Th>{t('fuel.table.station')}</Th>
                    <Th className="text-right">{t('fuel.table.liters')}</Th>
                    <Th className="text-right">{t('fuel.table.sar')}</Th>
                    <Th className="text-right">L/100km</Th>
                    <Th>{t('fuel.table.flags')}</Th>
                    <Th className="text-end w-12">{' '}</Th>
                  </tr>
                </thead>
                <tbody>
                  {list.items.map((tx) => (
                    <tr
                      key={tx.id}
                      className="border-t border-border hover:bg-accent/50 cursor-pointer"
                      onClick={() => setOpenTxId(tx.id)}
                    >
                      <Td>{new Date(tx.transaction_at).toLocaleString()}</Td>
                      <Td>
                        <Badge variant="inactive" className="text-xs">{tx.provider}</Badge>
                      </Td>
                      <Td>
                        {tx.vehicle_plate ?? tx.vehicle_code ?? '—'}
                        {tx.cost_attribution_movement_id ? (
                          <span className="ml-1 inline-flex items-center text-amber-600" title={t('fuel.table.reattributedTooltip') as string}>
                            <ArrowRightLeft className="h-3 w-3" />
                          </span>
                        ) : null}
                      </Td>
                      <Td>
                        {/* Matched driver first. Failing that, whoever the
                            assignment record says held this truck ON THAT DAY -
                            a recorded fact, bounded by effective_from and
                            effective_to, not today's assignment. The title says
                            which of the two you are reading, because a derived
                            name and a matched one are different claims. The
                            provider's card labels are gone entirely: they named
                            nobody who works here. */}
                        {tx.driver_name
                          ? tx.driver_name
                          : tx.assigned_driver_name
                            ? (
                              <span title="From the vehicle assignment on this date — not matched to the fill itself">
                                {tx.assigned_driver_name}
                                <span className="text-muted-foreground"> *</span>
                              </span>
                            )
                            : '—'}
                      </Td>
                      <Td>{tx.station_name ?? '—'}</Td>
                      <Td className="text-right">{fmtSar(tx.liters)}</Td>
                      <Td className="text-right">{fmtSar(tx.total_sar)}</Td>
                      <Td className="text-right">{tx.liters_per_100km && Number(tx.liters_per_100km) > 0 ? Number(tx.liters_per_100km).toFixed(2) : '—'}</Td>
                      <Td>
                        {tx.is_anomaly ? (
                          <Badge className="bg-red-500/15 text-red-700 border-red-500/30">
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            {t('fuel.table.anomaly')}
                          </Badge>
                        ) : null}
                      </Td>
                      <Td className="text-end">
                        <span onClick={(e: MouseEvent) => e.stopPropagation()}>
                          <RowDeleteButton
                            endpoint={`/api/v1/fuel-transactions/${tx.id}`}
                            entityKind="fuel transaction"
                            label={tx.external_id ?? tx.id.slice(0, 8)}
                            onDeleted={refetchFuel}
                          />
                        </span>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-3 py-2 text-xs text-muted-foreground bg-muted/30 border-t border-border">
              {t('fuel.table.summary', { count: list.items.length, total: list.total })}
            </div>
          </div>
        )}
      </main>

      {openTxId ? (
        <FuelTransactionDetailSheet id={openTxId} onClose={() => setOpenTxId(null)} />
      ) : null}
    </div>
  );
}

function StatCard({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <div className={`rounded-md border p-3 ${warn ? 'border-amber-500/40 bg-amber-500/5' : 'border-border bg-card'}`}>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`text-lg font-semibold mt-1 ${warn ? 'text-amber-700' : ''}`}>{value}</div>
    </div>
  );
}
function Th({ children, className }: { children: any; className?: string }) {
  return <th className={`px-3 py-2 text-left font-medium text-muted-foreground ${className ?? ''}`}>{children}</th>;
}
function Td({ children, className }: { children: any; className?: string }) {
  return <td className={`px-3 py-2 ${className ?? ''}`}>{children}</td>;
}
